import React, { useEffect } from 'react';
import { BRAND_NAME } from '../../theme/brand';
import {
    Box,
    Text,
    VStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    List,
    ListItem,
    ListIcon,
    OrderedList,
    Card,
    Divider,
    Flex,
    Tag,
    Center,
    Spinner
} from '@chakra-ui/react';

import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-json';
import Header from '../../components/Header';
// Bundled defaults; overridden per deployment by the env-driven public/ paths below.
import webhookImage from "../../images/webhook.png"
import webhookPopupImage from "../../images/webhookpopup.png"
import { WEBHOOK_DOC_IMAGE_SRC, WEBHOOK_POPUP_IMAGE_SRC } from '../../theme/brand';
import { BsFillCircleFill } from 'react-icons/bs';
import usePagePermissions from '../../hooks/usePagePermissions';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
const WebhookDocumentation = ({ header }) => {

    const { loading, can } = usePagePermissions('documentation/webhook-doc');

    const viewPermission = can("webhook-doc")
    useEffect(() => {
        Prism.highlightAll();
    }, []);

    const CodeBlock = ({ children, language = 'json' }) => (
        <Box
            as="pre"
            bg="gray.900"
            color="white"
            p={4}
            borderRadius="md"
            overflow="auto"
            fontSize="sm"
            fontFamily="mono"
        >
            <code className={`language-${language}`}>{children}</code>
        </Box>
    );

    const responseCodes = [
        {
            code: "200",
            status: "accepted",
            description: "Message has been accepted and will be sent to Customers",
            color: "green"
        },
        {
            code: "400",
            status: "Authentication Failed",
            description: "API Key Missing, Invalid API Key or API Key does not Exist in our Database",
            color: "red"
        },
        {
            code: "401",
            status: "Phone Number Missing",
            description: "No Field sent in Payload in Blank",
            color: "red"
        },
        {
            code: "403",
            status: "Insufficient balance",
            description: "Customer did not have enough balance available or has reached their Credit limit",
            color: "red"
        },
        {
            code: "404",
            status: "Content Missing",
            description: "Message field sent in Payload is Blank",
            color: "red"
        },
        {
            code: "402",
            status: "Account Inactive",
            description: "Customer Account Mark as Inactive in Our System",
            color: "orange"
        },
        {
            code: "500",
            status: "Server Error",
            description: "Internal server error - please retry",
            color: "red"
        },
        {
            code: "503",
            status: "Service Unavailable",
            description: "Service temporarily unavailable - maintenance or high load",
            color: "orange"
        }
    ];


    if (loading) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Center h="400px">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                </BoxWrapper>
            </>
        );
    }

    return (
        <>
            <Header header={header} />
            <BoxWrapper>
                {viewPermission ? <><Flex justify={"center"} align={"center"}>
                    <Box w={"70%"} mt={8}>
                        <Text fontSize={"4xl"} fontWeight={"semibold"}>DLR Webhooks
                        </Text>

                    </Box>
                </Flex>
                    <Flex justify={"center"} align={"center"}>
                        <Box w={"70%"} >
                            <Text color="gray.600" fontSize="md">
                                {BRAND_NAME} supports custom webhooks to send message acceptance and delivery updates in real time. This ensures that
                                your application stays in sync with our system's current messaging status.
                            </Text>

                        </Box>
                    </Flex>
                    <Divider bg={"#eaeaea"} my={3} />



                    <Flex justify={"center"} align={"center"} mt={4}>
                        <Box w={"70%"}>

                            <VStack spacing={8} align="stretch">

                                <Box>
                                    <Text fontSize="xl" fontWeight={"semibold"}>How to Configure Webhook URL</Text>
                                    <Divider my={2} />
                                    <Card mb="10px" p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                                        <Text color="gray.600" mb={4}>
                                            Go to Webhook Logs and click on Configure as in Webhook
                                        </Text>
                                        <img
                                            src={WEBHOOK_DOC_IMAGE_SRC || webhookImage}
                                            alt="Webhook Logs page with the Configure button"
                                            style={{ maxWidth: '100%' }}
                                        />

                                        <OrderedList spacing={2} color="gray.700">
                                            <ListItem>Enter the Webhook URL - it should be https URL</ListItem>
                                            <ListItem>Click on WEBHOOK button to save the Webhook URL</ListItem>
                                        </OrderedList>
                                        <img
                                            src={WEBHOOK_POPUP_IMAGE_SRC || webhookPopupImage}
                                            width={"50%"}
                                            alt="Configure Webhook URL dialog"
                                        />
                                    </Card>
                                </Box>



                                {/* Message Acceptance Response */}
                                <Box>
                                    <Text fontSize="xl" fontWeight={"semibold"}>1. Message Acceptance Response</Text>
                                    <Divider my={2} />

                                    <Card mb="10px" p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                                        <Text color="gray.600" mb={4}>
                                            When a customer sends a message request to {BRAND_NAME}, we immediately respond with a{' '}
                                            <Text as="span" fontWeight="bold">Message Acceptance Payload</Text>. This payload
                                            contains the file requests sent scheduled and is triggered in response to the customer initial request.
                                        </Text>

                                        <CodeBlock>
                                            {`{
    "messageId": "u5l3-
    "trackId": "12345",
    "status": "accepted",
    "timestamp": 1575374796
}`}
                                        </CodeBlock>
                                    </Card>
                                </Box>

                                {/* API Response Code Table */}
                                <Box>
                                    <Text fontSize="xl" fontWeight={"semibold"}>API Response Code</Text>
                                    <Divider my={2} />
                                    <TableContainer
                                        width="100%"
                                        bg="white"
                                        borderRadius="lg"
                                        border="1px solid"
                                        borderColor="gray.200"
                                        overflow="hidden"
                                    >
                                        <Table variant="simple" width="100%">
                                            <Thead bg="#f7f7f7">
                                                <Tr>
                                                    <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                                        Code
                                                    </Th>
                                                    <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                                        Status
                                                    </Th>
                                                    <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                                        Description
                                                    </Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody fontSize="14px">
                                                {responseCodes.map((item, index) => (
                                                    <Tr key={index} _hover={{ bg: "gray.50" }}>
                                                        <Td color="black" fontWeight="500" py={4} >
                                                            {item.code}
                                                        </Td>
                                                        <Td py={4}>
                                                            <Tag
                                                                bg={item.color === 'green' ? '#22c55e' : item.color === 'red' ? '#dc2626' : '#f97316'}
                                                                color="white"
                                                                fontSize="xs"
                                                            >
                                                                {item.status}
                                                            </Tag>
                                                        </Td>
                                                        <Td color="black" py={4} whiteSpace="normal" wordBreak="break-word">
                                                            {item.description}
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>
                                </Box>

                                {/* Delivery Status Webhook */}
                                <Box>
                                    <Text fontSize="xl" fontWeight={"semibold"}>2. Delivery Status Webhook</Text>
                                    <Divider my={2} />
                                    <Card mb="10px" p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                                        <Text color="gray.600" mb={2}>
                                            Delivery status webhook sent confirmation from the respective channel like the SMS, WhatsApp, Email, Push, etc. as soon as
                                            the message is sent with final delivery status for the message and final webhook acknowledgement.
                                        </Text>

                                        <CodeBlock>
                                            {`{
    "messageId": "TestSUL1-1545-9847-0690-xxxxxxxxx",
    "trackId": "12345",
    "status": "delivered",
    "timestamp": "1575374796",
    "channel": "sms"
}`}
                                        </CodeBlock>
                                    </Card>
                                </Box>

                                {/* Field Descriptions */}
                                <Box>
                                    <Text fontSize="xl" fontWeight={"semibold"}>Field Descriptions</Text>
                                    <Divider my={2} />
                                    <Card mb="10px" p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                                        <TableContainer
                                            width="100%"
                                            bg="white"
                                            borderRadius="lg"
                                            border="1px solid"
                                            borderColor="gray.200"
                                            overflow="hidden"
                                        >
                                            <Table variant="simple" width="100%">
                                                <Thead bg="#f7f7f7">
                                                    <Tr>
                                                        <Th fontSize="14px"
                                                            fontWeight="600"
                                                            color="black"
                                                            textTransform="none"
                                                            letterSpacing="normal"
                                                            py={4}>Field</Th>
                                                        <Th fontSize="14px"
                                                            fontWeight="600"
                                                            color="black"
                                                            textTransform="none"
                                                            letterSpacing="normal"
                                                            py={4}>Type</Th>
                                                        <Th fontSize="14px"
                                                            fontWeight="600"
                                                            color="black"
                                                            textTransform="none"
                                                            letterSpacing="normal"
                                                            py={4}>Description</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    <Tr>
                                                        <Td><>messageId</></Td>
                                                        <Td>String</Td>
                                                        <Td>Unique ID of the message (same as provided in acceptance response)</Td>
                                                    </Tr>
                                                    <Tr>
                                                        <Td><>status</></Td>
                                                        <Td>String</Td>
                                                        <Td>Delivery status of the message (e.g., Success, Failed, Read)</Td>
                                                    </Tr>
                                                    <Tr>
                                                        <Td><>details</></Td>
                                                        <Td>String</Td>
                                                        <Td>Additional details about the delivery (e.g., Message Sent, Delivered or Service Level, Invalid Number)</Td>
                                                    </Tr>
                                                    <Tr>
                                                        <Td><>datetime</></Td>
                                                        <Td>Integer</Td>
                                                        <Td>Unix timestamp (seconds) when the status was delivered</Td>
                                                    </Tr>
                                                    <Tr>
                                                        <Td><>channel</></Td>
                                                        <Td>String</Td>
                                                        <Td>Channel through which the message was delivered (e.g., SMS, WhatsApp, Email, Push)</Td>
                                                    </Tr>
                                                </Tbody>
                                            </Table>
                                        </TableContainer>

                                        <Box mt={6} p={4} bg="var(--brand-tint)" borderRadius="md" border={"1px solid var(--brand-tint-strong)"}>
                                            <Text as="span" fontWeight="semibold" color={"var(--brand-900)"} mb={2}>Message Lifecycle Tracking:</Text>
                                            <Text fontSize="sm" color="var(--brand-900)">
                                                {BRAND_NAME} sends a separate webhook for each delivery stage of a message. This allows customers to track the full lifecycle of their
                                                messages accurately and plan event triggers accordingly:
                                            </Text>
                                            <List mt={3} spacing={1}>
                                                <ListItem color="var(--brand-900)">
                                                    <ListIcon as={BsFillCircleFill} color={"var(--brand-900)"} boxSize={"12px"} />
                                                    <Text as="span" fontWeight="semibold" color={"var(--brand-900)"}>Message Sent</Text> - The message has been accepted by the channel provider
                                                </ListItem>
                                                <ListItem color="var(--brand-900)">
                                                    <ListIcon as={BsFillCircleFill} color={"var(--brand-900)"} boxSize={"12px"} />
                                                    <Text as="span" fontWeight="semibold" color={"var(--brand-900)"}>Message Delivered</Text> - The message has been delivered to the recipient's device
                                                </ListItem>
                                                <ListItem color="var(--brand-900)">
                                                    <ListIcon as={BsFillCircleFill} color={"var(--brand-900)"} boxSize={"12px"} />
                                                    <Text as="span" fontWeight="semibold" color={"var(--brand-900)"} >Message Read</Text> - The recipient has read/opened the message (where supported by the channel)
                                                </ListItem>
                                                <ListItem color="var(--brand-900)">
                                                    <ListIcon as={BsFillCircleFill} color={"var(--brand-900)"} boxSize={"12px"} />
                                                    <Text as="span" fontWeight="semibold" color={"var(--brand-900)"}>Message Failed</Text> - The message could not be delivered (e.g., invalid number, carrier issue, expired)
                                                </ListItem>
                                            </List>

                                            <VStack align="start" spacing={2} mt={4}>
                                                <Text fontSize="sm" fontWeight="semibold" color={"var(--brand-900)"}>Push / Inbox Specific Status</Text>
                                                <List fontSize="sm" spacing={1} color="var(--brand-900)">
                                                    <ListItem>• <Text as="span" fontWeight="semibold" color={"var(--brand-900)"}>Clicked</Text> - The recipient clicked on the message notification</ListItem>
                                                    <ListItem>• <Text as="span" fontWeight="semibold" color={"var(--brand-900)"}>Opened</Text> - The recipient opened or interacted with the specific app notification</ListItem>
                                                </List>
                                                <Text fontSize="xs" mt={2} color="var(--brand-900)">
                                                    Each update references the same messageId, so customers can update the state of a single message progressively as new
                                                    webhooks arrive.
                                                </Text>
                                            </VStack>
                                        </Box>


                                    </Card>
                                </Box>

                                <Text fontSize="xl" fontWeight={"semibold"}>3. Customer Webhook Response</Text>
                                <Card mb="10px" p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                                    <Box>

                                        <Text color="gray.600" mb={4}>
                                            Customer must acknowledge the webhook by sending a response. {BRAND_NAME} logs this response for monitoring and troubleshooting.
                                        </Text>

                                        <CodeBlock>
                                            {`{
    "status": "received",
    "code": "success"
}`}
                                        </CodeBlock>
                                    </Box>

                                    {/* Field Descriptions for Response */}
                                    <Box>
                                        <Text fontSize="xl" fontWeight={"semibold"}>Field Descriptions</Text>
                                        <TableContainer>
                                            <Table variant="simple">
                                                <Thead bg="gray.50">
                                                    <Tr>
                                                        <Th>Field</Th>
                                                        <Th>Type</Th>
                                                        <Th>Description</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    <Tr>
                                                        <Td><>code</></Td>
                                                        <Td>String</Td>
                                                        <Td>Status of the customer's response (e.g. Success, Failed)</Td>
                                                    </Tr>
                                                    <Tr>
                                                        <Td><>result</></Td>
                                                        <Td>String</Td>
                                                        <Td>HTTP status code received by the customer's endpoint.</Td>
                                                    </Tr>
                                                </Tbody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                </Card>
                                {/* Best Practices */}
                                <Box>
                                    <Text fontSize="xl" fontWeight={"semibold"}>4. Best Practices</Text>
                                    <Divider my={2} />
                                    <Card mb="10px" p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                                        <List spacing={3}>
                                            <ListItem>
                                                <ListIcon as={BsFillCircleFill} color={"var(--brand-900)"} boxSize={"10px"} />
                                                <Text as="span" fontWeight="semibold">Endpoint Availability:</Text> Ensure your webhook endpoint is publicly accessible and can handle high throughput.
                                            </ListItem>
                                            <ListItem>
                                                <ListIcon as={BsFillCircleFill} color={"var(--brand-900)"} boxSize={"10px"} />
                                                <Text as="span" fontWeight="semibold">Idempotency:</Text> Always use messageId as the unique identifier to avoid processing duplicate webhooks.
                                            </ListItem>
                                            <ListItem>
                                                <ListIcon as={BsFillCircleFill} color={"var(--brand-900)"} boxSize={"10px"} />
                                                <Text as="span" fontWeight="semibold">Response Time:</Text> Respond quickly (within 5 seconds) to webhooks to avoid retries and potential duplicate processing.
                                            </ListItem>
                                            <ListItem>
                                                <ListIcon as={BsFillCircleFill} color={"var(--brand-900)"} boxSize={"10px"} />
                                                <Text as="span" fontWeight="semibold">Error Handling:</Text> Implement proper error handling and logging to capture any issues for investigations.
                                            </ListItem>
                                        </List>
                                    </Card>
                                </Box>

                                {/* Workflow Summary */}
                                <Box mb={8}>
                                    <Text fontSize="xl" fontWeight={"semibold"}>5. Workflow Summary</Text>
                                    <Divider my={2} />
                                    <Card mb="10px" p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                                        <OrderedList spacing={2}>
                                            <ListItem>Customer sends message to {BRAND_NAME} responds with <Text as="span" fontWeight="bold">Acceptance Payload</Text></ListItem>
                                            <ListItem>{BRAND_NAME} delivers the message to the chosen channel.</ListItem>
                                            <ListItem>Channel provides delivery confirmation via <Text as="span" fontWeight="bold">Delivery Status Webhook</Text></ListItem>
                                            <ListItem>Customer responds with <Text as="span" fontWeight="bold">200 OK</Text> or {BRAND_NAME} logs customer's response.</ListItem>
                                        </OrderedList>
                                    </Card>
                                </Box>


                            </VStack>

                        </Box>
                    </Flex></> : <BoxWrapper>
                    <Center h="400px">
                        <Box textAlign="center">
                            <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                                No Access
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                You don't have permission to view this sections.
                            </Text>
                        </Box>
                    </Center>
                </BoxWrapper>}

            </BoxWrapper>
        </>
    );
};

export default WebhookDocumentation;